import { makeFunctionReference } from "convex/server"

/**
 * Public queries on the Rivyu Convex deployment (same backend as the Rivyu Telegram Mini App).
 * `makeFunctionReference` avoids checking in `convex/_generated/api` from this repo.
 */

/** Subset of `admin.getGlobalStats` used by the marketing site. */
export type RivyuGlobalStatsPublic = {
  totalUsers: number
  /** Mining pool rewards paid + staking pool rewards paid (active users only). */
  totalMined: number
  totalStaked: number
  /** Mining pool rewards paid to non-ignored users. */
  miningPoolPaidOut?: number
  /** Staking pool rewards paid to non-ignored users. */
  stakingPoolPaidOut?: number
  totalMinedMiningPortion?: number
  totalMinedStakingPortion?: number
  /** Distinct wallets with active stake when exposed by Convex. */
  uniqueStakers?: number
  /** Preferred for "miners online" / active wallets when exposed by Convex. */
  currentActiveUsers?: number
  activeUsers?: number
  activeUsers24h?: number
  blockedUsers?: number
  miningPoolPercent?: number
  miningPoolBalance?: number
  updatedAt?: number
}

/** Subset of `admin.getUtilityPoolStats`. */
export type RivyuUtilityPoolStatsPublic = {
  total: number
  levelUps: number
  levelUpCount: number
  emergencyExits: number
  emergencyExitCount: number
}

/** Protocol pool figures from `admin:getGlobalStats` with investor-cache fallbacks. */
export function pickProtocolPoolMetrics(
  global: RivyuGlobalStatsPublic | undefined,
  investor: RivyuInvestorStatsPublic | null | undefined,
  utility: RivyuUtilityPoolStatsPublic | undefined,
) {
  const miningPoolPaid =
    global?.miningPoolPaidOut ??
    global?.totalMinedMiningPortion ??
    investor?.poolMetrics?.miningPoolRewardsPaidRiv ??
    investor?.mining?.totalRivMinedMiningPortion ??
    0
  const stakingPoolPaid =
    global?.stakingPoolPaidOut ??
    global?.totalMinedStakingPortion ??
    investor?.poolMetrics?.stakingPoolRewardsPaidRiv ??
    investor?.staking?.stakingPoolRewardsPaidRiv ??
    investor?.mining?.totalRivMinedStakingPortion ??
    0
  const totalMined =
    global?.totalMined ??
    (miningPoolPaid > 0 || stakingPoolPaid > 0 ? miningPoolPaid + stakingPoolPaid : 0)
  const totalStaked =
    global?.totalStaked ?? investor?.staking?.totalTvlRiv ?? investor?.poolMetrics?.totalStakedRiv ?? 0
  const utilityPoolTotal =
    utility?.total ?? investor?.utilityPool?.totalRiv ?? investor?.poolMetrics?.utilityPoolTotalRiv ?? 0
  const poolPct = Math.min(100, Math.max(0, global?.miningPoolPercent ?? investor?.poolMetrics?.miningPoolDistributedPct ?? 0))

  return {
    totalMined,
    miningPoolPaid,
    totalStaked,
    stakingPoolPaid,
    utilityPoolTotal,
    poolPct,
  }
}

/** Prefer explicit active-user counters; fall back to `totalUsers` for older deployments. */
export function pickCurrentActiveUsers(g: RivyuGlobalStatsPublic | undefined): number {
  if (!g) return 0
  return (
    g.currentActiveUsers ??
    g.activeUsers24h ??
    g.activeUsers ??
    g.totalUsers ??
    0
  )
}

/** Subset of `chests.getEcosystemMetrics` used by the marketing site. */
export type RivyuEcosystemMetricsPublic = {
  totalBurned: number
  syncing?: boolean
  discrepancyDetected?: boolean
}

export const rivyuConvexGlobalStats = makeFunctionReference<
  "query",
  Record<string, never>,
  RivyuGlobalStatsPublic
>("admin:getGlobalStats")

export const rivyuConvexUtilityPoolStats = makeFunctionReference<
  "query",
  Record<string, never>,
  RivyuUtilityPoolStatsPublic
>("admin:getUtilityPoolStats")

export const rivyuConvexEcosystemMetrics = makeFunctionReference<
  "query",
  Record<string, never>,
  RivyuEcosystemMetricsPublic
>("chests:getEcosystemMetrics")

/** Public `ideaPad.getIdeaPadStats` — community idea submissions (DApp + marketing site). */
export type RivyuIdeaPadStatsPublic = {
  totalSubmitted: number
  approved: number
  implemented: number
  rejected: number
  inVoting: number
}

export const rivyuConvexIdeaPadStats = makeFunctionReference<
  "query",
  Record<string, never>,
  RivyuIdeaPadStatsPublic
>("ideaPad:getIdeaPadStats")

/** Public `bugBounty.getBugBountyStats` — reports and RIV obligations vs paid-out after pool verification. */
export type RivyuBugBountyStatsPublic = {
  totalReports: number
  resolved: number
  confirmed: number
  rejected: number
  rivRewardsAwarded: number
  rivRewardsPaidOut: number
}

export const rivyuConvexBugBountyStats = makeFunctionReference<
  "query",
  Record<string, never>,
  RivyuBugBountyStatsPublic
>("bugBounty:getBugBountyStats")

/** Cached payload from `investorStats.getPublicInvestorStats` (refreshed every 30m on the DApp backend). */
export type RivyuRetentionCohort = {
  cohortSize: number
  retained: number
  pct: number
}

export type RivyuInvestorStatsPublic = {
  snapshotAt: string
  cacheUpdatedAt?: number
  users: {
    total: number
    walletConnected: number
    walletConnectedPct: number
    guests: number
    ignoredUsers?: number
    emailVerified?: number
    missingLastActiveAt?: number
    withLastActiveAt?: number
  }
  growth: {
    newUsers24h: number
    newUsers7d: number
    newUsers30d: number
  }
  engagement: {
    dau: number
    wau: number
    mau: number
    dauCalendarToday: number
    stickinessDauOverMau: number
    walletConnectedDau: number
    dauWhoAreMining: number
    churnedLast7d?: number
    dauOverWithLastActivePct?: number
    claimsLast24h?: number
    claimsLast7d?: number
    claimsLast30d?: number
  }
  retention: {
    d1: RivyuRetentionCohort
    d7: RivyuRetentionCohort
    d30: RivyuRetentionCohort
  }
  mining: {
    totalRivMinedCumulative: number
    totalRivMinedMiningPortion?: number
    totalRivMinedStakingPortion?: number
    activeSessions: number
    uniqueActiveMiners: number
    usersWhoEverMined: number
  }
  poolMetrics?: {
    miningPoolRewardsPaidRiv?: number
    stakingPoolRewardsPaidRiv?: number
    utilityPoolTotalRiv?: number
    totalStakedRiv?: number
    miningPoolDistributedPct?: number
    activeUsersOnly?: boolean
  }
  utilityPool?: {
    totalRiv: number
    levelUpsRiv?: number
    emergencyExitsRiv?: number
  }
  staking: {
    totalTvlRiv: number
    uniqueStakers: number
    stakingPoolRewardsPaidRiv?: number
    tvlByPool?: Record<string, number>
  }
  chests: {
    totalAwarded: number
    totalOpened: number
    blackMarketPurchases?: number
    totalRivSpentOnChests?: number
    totalRivBurnedOnChain: number
    totalRivBurnedComputed50pct?: number
  }
  monetization?: {
    arpdauRivBurned: number
  }
  referrals: {
    totalCommissionRiv?: number
    uniqueReferrers: number
  }
  community?: {
    ideaPadIdeas: number
    bugBountyReports: number
  }
}

/** Signed GET URL for the published whitepaper PDF in Convex Storage (short-lived). */
export const rivyuConvexWhitepaperUrl = makeFunctionReference<
  "query",
  Record<string, never>,
  string | null
>("whitepaper:getDownloadUrl")

export const rivyuConvexInvestorStats = makeFunctionReference<
  "query",
  Record<string, never>,
  RivyuInvestorStatsPublic | null
>("investorStats:getPublicInvestorStats")
